import { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Calendar as CalendarIcon, Check, ArrowLeft, AlertCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CLIENT_TZ = (() => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch {
    return "UTC";
  }
})();

const formatLocalTime = (date, startTime, endTime) => {
  try {
    const startUtc = new Date(`${date}T${startTime}:00Z`);
    const endUtc = new Date(`${date}T${endTime}:00Z`);
    const fmt = new Intl.DateTimeFormat(undefined, {
      hour: "2-digit",
      minute: "2-digit",
      timeZone: CLIENT_TZ,
    });
    const tzAbbr =
      new Intl.DateTimeFormat(undefined, { timeZone: CLIENT_TZ, timeZoneName: "short" })
        .formatToParts(startUtc)
        .find((p) => p.type === "timeZoneName")?.value || CLIENT_TZ;
    return `${fmt.format(startUtc)} – ${fmt.format(endUtc)} (${tzAbbr})`;
  } catch {
    return null;
  }
};

export default function ReschedulePage() {
  const { bookingId } = useParams();
  const [params] = useSearchParams();
  const token = params.get("t") || "";

  const [status, setStatus] = useState("loading"); // loading | ready | error | done
  const [errorMsg, setErrorMsg] = useState("");
  const [booking, setBooking] = useState(null);
  const [slots, setSlots] = useState([]);
  const [selected, setSelected] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [confirmed, setConfirmed] = useState(null);

  useEffect(() => {
    if (!token) {
      setErrorMsg("Missing reschedule token in link.");
      setStatus("error");
      return;
    }
    (async () => {
      try {
        const [infoRes, slotRes] = await Promise.all([
          axios.get(`${API}/booking/reschedule-info/${bookingId}`, { params: { t: token } }),
          axios.get(`${API}/availability/available`),
        ]);
        setBooking(infoRes.data);
        setSlots(slotRes.data.filter((s) => s.id !== infoRes.data.slot_id));
        setStatus("ready");
      } catch (err) {
        setErrorMsg(err.response?.data?.detail || "This reschedule link is no longer valid.");
        setStatus("error");
      }
    })();
  }, [bookingId, token]);

  const grouped = useMemo(() => {
    const map = new Map();
    const horizon = new Date();
    horizon.setDate(horizon.getDate() + 21);
    for (const s of slots) {
      if (!s.date) continue;
      if (new Date(`${s.date}T00:00:00Z`) > horizon) continue;
      if (!map.has(s.date)) map.set(s.date, []);
      map.get(s.date).push(s);
    }
    for (const arr of map.values()) arr.sort((a, b) => a.start_time.localeCompare(b.start_time));
    return [...map.entries()].sort(([a], [b]) => a.localeCompare(b));
  }, [slots]);

  const submit = async () => {
    if (!selected) {
      toast.error("Pick a new slot first");
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/booking/reschedule/${bookingId}`, {
        t: token,
        new_slot_id: selected.id,
      });
      setConfirmed(res.data);
      setStatus("done");
      toast.success("Session rescheduled");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not reschedule — pick a different slot.");
    } finally {
      setSubmitting(false);
    }
  };

  if (status === "loading") {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#D4AF37]" />
      </div>
    );
  }

  if (status === "error") {
    const cutoff = /less than \d+ hours/i.test(errorMsg);
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <div
          className={`max-w-md ${cutoff ? "border-yellow-500/40 bg-yellow-500/[0.04]" : "border-red-500/30 bg-red-500/[0.05]"} border p-8 text-center`}
          data-testid="reschedule-error"
        >
          <AlertCircle className={`w-10 h-10 ${cutoff ? "text-yellow-400" : "text-red-400"} mx-auto mb-4`} />
          <h1 className="font-serif text-2xl text-white mb-3">{cutoff ? "Too close to move" : "Cannot reschedule"}</h1>
          <p className="text-slate-300 text-sm leading-relaxed mb-6">{errorMsg}</p>
          {cutoff && (
            <p className="text-slate-500 text-xs mb-6">
              Email <a href="mailto:info@badbossclinic.com" className="text-[#D4AF37]">info@badbossclinic.com</a> and we'll move it manually.
            </p>
          )}
          <a
            href="/"
            className="inline-flex items-center gap-2 border border-slate-700 text-slate-300 px-5 py-2.5 text-xs uppercase tracking-widest hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors"
          >
            <ArrowLeft className="w-3 h-3" /> Back to Bad Boss Clinic
          </a>
        </div>
      </div>
    );
  }

  if (status === "done") {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <div className="max-w-md border border-[#D4AF37]/40 bg-[#D4AF37]/[0.05] p-8 text-center" data-testid="reschedule-done">
          <div className="inline-flex items-center justify-center w-14 h-14 border border-[#D4AF37] bg-[#D4AF37]/20 mb-4">
            <Check className="w-7 h-7 text-[#D4AF37]" />
          </div>
          <h1 className="font-serif text-2xl text-white mb-3">Session rescheduled</h1>
          <p className="text-slate-300 text-sm mb-2">
            <strong className="text-white">{confirmed.date}</strong> · {confirmed.start_time} – {confirmed.end_time} UTC
          </p>
          {CLIENT_TZ !== "UTC" && (
            <p className="text-slate-500 text-xs mb-6">
              Your local time: {formatLocalTime(confirmed.date, confirmed.start_time, confirmed.end_time)}
            </p>
          )}
          <p className="text-slate-400 text-xs">A fresh confirmation email is on its way to your inbox.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-12 px-4" data-testid="reschedule-page">
      <div className="max-w-3xl mx-auto">
        <a
          href="/"
          className="inline-flex items-center gap-2 text-slate-400 hover:text-white text-xs uppercase tracking-widest mb-6 transition-colors"
        >
          <ArrowLeft className="w-3 h-3" /> Bad Boss Clinic
        </a>

        <div className="border border-slate-800 bg-slate-900/50 p-6 md:p-8 mb-6">
          <p className="text-[10px] uppercase tracking-[0.24em] text-[#D4AF37] mb-2">Reschedule</p>
          <h1 className="font-serif text-3xl text-white mb-4">Pick a new time for your session</h1>
          <div className="border-t border-slate-800 pt-4 grid sm:grid-cols-2 gap-4">
            <div>
              <p className="text-slate-400 text-xs uppercase tracking-widest mb-1">Booked by</p>
              <p className="text-white text-sm">{booking.client_name}</p>
              <p className="text-slate-500 text-xs">{booking.client_email}</p>
            </div>
            <div>
              <p className="text-slate-400 text-xs uppercase tracking-widest mb-1">Session</p>
              <p className="text-white text-sm capitalize">
                {booking.session_type === "initial" ? "Initial consultation" : "Follow-up session"} · €{booking.price_eur}
              </p>
            </div>
            <div className="sm:col-span-2 pt-2 border-t border-slate-900">
              <p className="text-slate-400 text-xs uppercase tracking-widest mb-1">Current slot</p>
              <p className="text-white text-lg" data-testid="current-slot">
                {booking.date} · {booking.start_time} – {booking.end_time} UTC
              </p>
              {CLIENT_TZ !== "UTC" && (
                <p className="text-slate-500 text-sm">
                  Your local time: {formatLocalTime(booking.date, booking.start_time, booking.end_time)}
                </p>
              )}
            </div>
          </div>
        </div>

        <div className="border border-slate-800 bg-slate-900/50 p-6 md:p-8">
          <h2 className="font-serif text-xl text-white mb-6 flex items-center gap-2">
            <CalendarIcon className="w-4 h-4 text-[#D4AF37]" />
            Available slots
          </h2>
          {grouped.length === 0 ? (
            <p className="text-slate-500 text-sm py-8 text-center">
              No available slots right now. Please email the clinic to reschedule manually.
            </p>
          ) : (
            <div className="space-y-6">
              {grouped.map(([date, arr]) => (
                <div key={date} data-testid={`reschedule-day-${date}`}>
                  <p className="text-slate-400 text-xs uppercase tracking-widest mb-3">{date}</p>
                  <div className="flex flex-wrap gap-2">
                    {arr.map((s) => {
                      const active = selected?.id === s.id;
                      return (
                        <button
                          key={s.id}
                          onClick={() => setSelected(s)}
                          data-testid={`reschedule-slot-${s.id}`}
                          className={`px-4 py-2.5 text-sm font-mono border transition-colors ${
                            active
                              ? "border-[#D4AF37] bg-[#D4AF37]/10 text-[#D4AF37]"
                              : "border-slate-700 text-slate-300 hover:border-[#D4AF37]/60 hover:text-white"
                          }`}
                        >
                          {s.start_time} – {s.end_time}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}

          {selected && (
            <div className="mt-8 pt-6 border-t border-slate-800 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <p className="text-slate-400 text-xs uppercase tracking-widest mb-1">New slot</p>
                <p className="text-white text-lg" data-testid="reschedule-new-slot">
                  {selected.date} · {selected.start_time} – {selected.end_time} UTC
                </p>
                {CLIENT_TZ !== "UTC" && (
                  <p className="text-slate-500 text-xs">
                    Your local time: {formatLocalTime(selected.date, selected.start_time, selected.end_time)}
                  </p>
                )}
              </div>
              <button
                onClick={submit}
                disabled={submitting}
                data-testid="reschedule-confirm-btn"
                className="inline-flex items-center gap-2 bg-[#D4AF37] text-slate-950 px-6 py-3 text-xs uppercase tracking-widest font-medium hover:bg-[#B5952F] disabled:opacity-50 transition-colors"
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                Confirm reschedule
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
